import React, { useState, useEffect } from 'react';
import { listPasskeys, deletePasskey, PasskeyInfo, completePasskeyRegistration, isOk } from '../../api/auth/passkey_actions';
import { getDeviceName } from '../../utils/webauthn';
import usePasswordConfirmation from '../../hooks/usePasswordConfirmation';
import './PasskeyManagement.css';

// Inline icons (fill follows the CSS `color` of the parent) to avoid asset-specific quirks.
const KeyIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%" aria-hidden="true">
        <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
    </svg>
);
const PlusIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%" aria-hidden="true">
        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%" aria-hidden="true">
        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
);

interface PasskeyManagementProps {
    onError: (error: string) => void;
    onSuccess: (message: string) => void;
}

const PasskeyManagement: React.FC<PasskeyManagementProps> = ({ onError, onSuccess }) => {
    const [passkeys, setPasskeys] = useState<PasskeyInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [deletingCredentialId, setDeletingCredentialId] = useState<string | null>(null);
    const { confirm: confirmPassword } = usePasswordConfirmation();

    useEffect(() => {
        loadPasskeys();
    }, []);

    const loadPasskeys = async () => {
        try {
            setLoading(true);
            const result = await listPasskeys();

            if (isOk(result.Code)) {
                setPasskeys(result.Msg as PasskeyInfo[]);
            } else {
                onError(typeof result.Msg === "string" ? result.Msg : "Failed to load passkeys");
            }
        } catch (error: any) {
            console.error("Error loading passkeys:", error);
            onError("Failed to load passkeys");
        } finally {
            setLoading(false);
        }
    };

    const handleAddPasskey = () => {
        confirmPassword(async (password: string) => {
            await handleRegistrationWithPassword(password);
        }, {
            title: "Register Passkey",
            message: "Please confirm your password to register a new passkey."
        });
    };

    const handleDeletePasskey = (credentialId: string) => {
        confirmPassword(async (password: string) => {
            await performDelete(credentialId, password);
        }, {
            title: "Delete Passkey",
            message: "Please confirm your password to delete this passkey."
        });
    };

    const handleRegistrationWithPassword = async (password: string) => {
        try {
            const deviceName = getDeviceName();
            const result = await completePasskeyRegistration(deviceName, password);

            if (isOk(result.Code)) {
                onSuccess("Passkey registered successfully!");
                await loadPasskeys(); // Reload the list
            } else {
                onError(typeof result.Msg === "string" ? result.Msg : "Failed to register passkey");
            }
        } catch (error: any) {
            console.error("Error registering passkey:", error);
            onError(error.message || "Failed to register passkey");
        }
    };

    const performDelete = async (credentialId: string, password: string) => {
        try {
            setDeletingCredentialId(credentialId);
            const result = await deletePasskey(credentialId, password);

            if (isOk(result.Code)) {
                onSuccess("Passkey deleted successfully");
                await loadPasskeys(); // Reload the list
            } else {
                onError(typeof result.Msg === "string" ? result.Msg : "Failed to delete passkey");
            }
        } catch (error: any) {
            console.error("Error deleting passkey:", error);
            onError("Failed to delete passkey");
        } finally {
            setDeletingCredentialId(null);
        }
    };


    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return 'Unknown';
        }
    };

    if (loading) {
        return (
            <div className="passkey-management">
                <div className="pk-loading">
                    <div className="pk-spinner"></div>
                    <p style={{ margin: 0 }}>Loading passkeys...</p>
                </div>
            </div>
        );
    }

    const hasPasskeys = !!(passkeys && passkeys.length > 0);

    return (
        <div className="passkey-management">
            <div className="pk-header">
                <div className="pk-header-text">
                    <h4 className="pk-header-title">Your Passkeys</h4>
                    <p className="pk-header-subtitle">
                        Sign in securely using your device's biometrics or a security key — no password needed.
                    </p>
                </div>
                <button className="pk-add-btn" onClick={handleAddPasskey}>
                    <span className="pk-add-icon"><PlusIcon /></span>
                    Add Passkey
                </button>
            </div>

            {!hasPasskeys ? (
                <div className="pk-empty">
                    <div className="pk-empty-icon">
                        <KeyIcon />
                    </div>
                    <p className="pk-empty-title">No passkeys yet</p>
                    <p className="pk-empty-subtitle">
                        Add a passkey to enable quick and secure sign-in with your device's biometric features.
                    </p>
                </div>
            ) : (
                <div className="pk-list">
                    {passkeys.map((passkey) => {
                        const isDeleting = deletingCredentialId === passkey.credential_id;
                        return (
                            <div key={passkey.credential_id} className="pk-card">
                                <div className="pk-card-icon">
                                    <KeyIcon />
                                </div>
                                <div className="pk-card-body">
                                    <span className="pk-card-name">{passkey.device_name || "Unnamed device"}</span>
                                    <div className="pk-card-meta">
                                        <span>Added {formatDate(passkey.created_at)}</span>
                                        <span className="dot">•</span>
                                        <span>Last used {formatDate(passkey.last_used_at)}</span>
                                    </div>
                                </div>
                                <button
                                    className="pk-delete"
                                    onClick={() => handleDeletePasskey(passkey.credential_id)}
                                    disabled={isDeleting}
                                    title="Delete passkey"
                                    aria-label="Delete passkey"
                                >
                                    {isDeleting ? <span className="pk-spinner" /> : <TrashIcon />}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PasskeyManagement;